import { useContext } from "react";
import { useTranslation } from "@coworker/locales";

import { TasksSettingsContext } from "../../../context/tasksSettingsContext";
import type { ChangeTaskSettingsAction } from "../../../context/tasksSettingsReducer";

import FixaButton from "../../../../shared/wrappers/FixaButton/FixaButton";
import { FixaListView } from "../../../../shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "../../../../shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "../../../../shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "../../../../shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "../../../../shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "../../../../shared/wrappers/FixaModal/FixaSheets";
import styled from "styled-components";

const StyledFixaModalHeader = styled(FixaModalHeader)`
  border-bottom: 1px solid var(--grey200);
`;

const FooterContainer = styled.div`
  padding: 0 20px 20px 20px;
`;

const StyledFixaModalBody = styled(FixaModalBody)`
  padding: 0;
`;

const StyledFixaListView = styled(FixaListView)`
  padding-bottom: 20px;
`;

const StyledFixaListViewItem = styled(FixaListViewItem)`
  padding: 0 20px;
`;

const TasksSettingsModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  const dispatch = useContext(TasksSettingsContext).dispatch;
  const tasksSettings = useContext(TasksSettingsContext).tasksSettings;
  const changeTasksSettings = (
    type: ChangeTaskSettingsAction["type"],
    payload: boolean
  ) => {
    dispatch({
      type,
      payload,
    });
  };

  return (
    <FixaModal visible={show} handleCloseBtn={onClose}>
      <FixaSheets
        labelledById="multi-order-addons-task-settings-header"
        alignment={"right"}
        preserveAlignment={false}
        fullSize={false}
        header={
          <StyledFixaModalHeader
            id="multi-order-addons-task-settings-header"
            title={t("tasksSettingsString")}
            floating={false}
            closeBtnClick={onClose}
          />
        }
        footer={
          <FooterContainer>
            <FixaButton
              text={t("confirmString")}
              type="primary"
              fluid
              onClick={onClose}
            />
          </FooterContainer>
        }
      >
        <StyledFixaModalBody>
          <StyledFixaListView
            showDivider
            id="multi-order-tasks-settings-options"
          >
            <StyledFixaListViewItem
              onChange={() => {
                changeTasksSettings(
                  "MARK_AS_COMPLETED_SETTING",
                  !tasksSettings.markAsCompleted
                );
              }}
              control="switch"
              controlProps={{
                value: tasksSettings.markAsCompleted ? "ON" : "OFF",
                checked: tasksSettings.markAsCompleted,
              }}
              title={t("markAsCompletedForAllString")}
              data-testid={"markAsCompleted"}
            />
            <StyledFixaListViewItem
              onChange={() => {
                changeTasksSettings(
                  "MARK_AS_PRIORITY_SETTING",
                  !tasksSettings.markAsPriority
                );
              }}
              control="switch"
              controlProps={{
                value: tasksSettings.markAsPriority ? "ON" : "OFF",
                checked: tasksSettings.markAsPriority,
              }}
              title={t("markAsPriorityForAllString")}
              data-testid={"markAsPriority"}
            />
          </StyledFixaListView>
        </StyledFixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};

export default TasksSettingsModal;
